import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Link } from 'react-router-dom';
import { getCalculatorConfig } from '../../../services/contentApi';

const SmartToolsPreview = () => {
  const [inputs, setInputs] = useState({
    plotArea: '',
    areaType: 'Non-Congested', // Congested / Non-Congested
    metroZone: 'No', // Yes / No
    roadWidth: '',
    buildingType: 'Residential', // Residential / Commercial / Mixed
    sanctionedLayout: false,
    gunthewari: false,
    openSpaceLeft: false,
    willingToPayChallan: false,
    redevelopment: 'No',
    previouslyConsumedBuiltUpArea: '', // For redevelopment calculation
    numberOfTenements: '', // For redevelopment calculation
    deductionRoadWidening: '',
    deductionDPRoad: '',
    deductionReservation: ''
  });

  const [showResults, setShowResults] = useState(false);
  const [calculationResult, setCalculationResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [calcConfig, setCalcConfig] = useState(null);

  // Load calculator config from Firestore (slug: fsi-calculator)
  useEffect(() => {
    (async () => {
      try {
        const cfg = await getCalculatorConfig('fsi-calculator');
        if (cfg) setCalcConfig(cfg);
      } catch (e) {
        // ignore, fallback to defaults
      }
    })();
  }, []);

  const sqmToSqft = (sqm) => sqm * 10.764;

  const validateForm = () => {
    const newErrors = {};
    if (!inputs.plotArea || parseFloat(inputs.plotArea) <= 0) newErrors.plotArea = 'Valid plot area is required';
    if (!inputs.roadWidth || parseFloat(inputs.roadWidth) < 0) newErrors.roadWidth = 'Road width is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const calculateFSI = () => {
    if (!validateForm()) return;

    const plotArea = parseFloat(inputs.plotArea) || 0;
    const roadWidth = parseFloat(inputs.roadWidth) || 0;

    const dedRoad = parseFloat(inputs.deductionRoadWidening) || 0;
    const dedDP = parseFloat(inputs.deductionDPRoad) || 0;
    const dedRes = parseFloat(inputs.deductionReservation) || 0;

    // Apply deductions ONLY to Basic FSI. Additions use full plot area.
    const deductionTotal = (dedRoad + dedDP + dedRes);
    // Gross area after road widening and other deductions
    const grossArea = Math.max(0, plotArea - deductionTotal);
    const netAreaAdditions = plotArea;

    let baseFSI = 0;
    let paidFSI = 0;
    let tdrFSI = 0;
    let redevelopmentFSI = 0;
    let additionalFSI = 0;

    // Industrial building type logic
    if (inputs.buildingType === 'Industrial') {
      if (roadWidth >= 9) {
        baseFSI = 1.00;
        paidFSI = 0.40;
        // Total FSI = 1.40 (1.00 + 0.40)
      } else {
        // For road width < 9m, show error message
        baseFSI = 0;
        paidFSI = 0;
        setErrors({ ...errors, roadWidth: 'Industrial building type requires Access Road Width of 9 meters or above.' });
        return; // Exit early to prevent calculation
      }
    }
    // TOD/METRO mapping
    else if (inputs.metroZone === 'Yes') {
      // TOD table
      if (inputs.areaType === 'Congested') {
        baseFSI = roadWidth < 9 ? 1.50 : 2.00;
      } else {
        baseFSI = 1.1;
      }

      let metroAdd = 0;
      if (roadWidth >= 9 && roadWidth < 12) {
        metroAdd = 1.4;
      } else if (roadWidth >= 12 && roadWidth < 15) {
        metroAdd = 1.9;
      } else if (roadWidth >= 15 && roadWidth < 24) {
        metroAdd = 2.4;
      } else if (roadWidth >= 24) {
        metroAdd = 2.9;
      }

      paidFSI = metroAdd * 0.75;
      tdrFSI = metroAdd * 0.25;
    } else if (inputs.areaType === 'Non-Congested') {
      if (roadWidth < 9) {
        baseFSI = 1.10;
      } else if (roadWidth >= 9 && roadWidth < 12) {
        baseFSI = 1.10; paidFSI = 0.50; tdrFSI = 0.40;
      } else if (roadWidth >= 12 && roadWidth < 15) {
        baseFSI = 1.10; paidFSI = 0.50; tdrFSI = 0.65;
      } else if (roadWidth >= 15 && roadWidth < 24) {
        baseFSI = 1.10; paidFSI = 0.50; tdrFSI = 0.90;
      } else if (roadWidth >= 24 && roadWidth < 30) {
        baseFSI = 1.10; paidFSI = 0.50; tdrFSI = 1.15;
      } else if (roadWidth >= 30) {
        baseFSI = 1.10; paidFSI = 0.50; tdrFSI = 1.40;
      }
    } else if (inputs.areaType === 'Congested') {
      if (roadWidth < 9) {
        baseFSI = 1.50;
      } else if (roadWidth >= 9 && roadWidth < 18) {
        baseFSI = 2.00; paidFSI = 0.30; tdrFSI = 0.30;
      } else if (roadWidth >= 18 && roadWidth < 30) {
        baseFSI = 2.00; paidFSI = 0.30; tdrFSI = 0.50;
      } else if (roadWidth >= 30) {
        baseFSI = 2.00; paidFSI = 0.30; tdrFSI = 0.70;
      }
    }

    // Redevelopment calculation - consider whichever is more:
    // a) Previously consumed Built up Area (As per Blueprint) x 30%
    // b) No. of tenaments x 15
    if (inputs.redevelopment === 'Yes' || (typeof inputs.redevelopment === 'string' && inputs.redevelopment.startsWith('Yes'))) {
      const previouslyConsumedArea = parseFloat(inputs.previouslyConsumedBuiltUpArea) || 0;
      const numberOfTenements = parseFloat(inputs.numberOfTenements) || 0;

      const optionA = previouslyConsumedArea * 0.30; // 30% of previously consumed built-up area
      const optionB = numberOfTenements * 15; // Number of tenements x 15

      // Consider whichever is more
      redevelopmentFSI = Math.max(optionA, optionB);

      // If no inputs provided, default to 1.00 (backward compatibility)
      if (previouslyConsumedArea === 0 && numberOfTenements === 0) {
        redevelopmentFSI = 1.00;
      }
    }

    // Full FSI conditions base setting: any selected → 1.10, else → 0.825
    //  const anyFullFsiSelected = (
    //   inputs.sanctionedLayout ||
    //   inputs.gunthewari ||
    //   inputs.openSpaceLeft ||
    //   inputs.willingToPayChallan
    // );
    // baseFSI = anyFullFsiSelected ? 1.10 : 0.825;
    // If no prior branch set a baseFSI, default to 1.10
    if (baseFSI === 0) {
      baseFSI = 1.10;
    }

    // Open Space requirement:
    // If NOT Sanctioned Layout AND (Gross Area > 4000 OR User explicitly opted for 10% open space)
    // Note: User opt-in for 10% open space is currently not a direct input checkbox in the code provided, 
    // but the Excel suggests "I will leave 10% open space" is an option. 
    // Based on current inputs, we'll stick to the area rule and Sanctioned Layout check.
    // Excel Formula: IF(M8="Y", "No",IF(H14>=4000,"Yes",IF(M10="Y","Yes","No")))
    // M8 = Sanctioned Layout
    // H14 = Gross Area
    // M10 = "I will leave 10% open space" (Not currently tracked in state as a specific independent input besides 'openSpaceLeft' checkbox which might be it)

    let openSpaceRequired = 0;
    const sanctionedInput = inputs.sanctionedLayout; // "My Plot is from Sanctioned Layout"

    // Amenity space requirement: 
    // Excel Formula: IF(M8="Yes","No",IF(H14>=20000,"Yes","No"))
    // Required if NOT Sanctioned Layout AND Gross Area >= 20000
    let amenitySpaceRequired = 0;
    if (!sanctionedInput && grossArea >= 20000) {
      amenitySpaceRequired = grossArea * 0.05; // 5% of gross area
    }

    // Net area for Basic FSI = Gross area - Amenity space (Open space area is no longer deducted as per requirements)
    const netAreaBase = Math.max(0, grossArea - amenitySpaceRequired);

    // Net plot area (after deductions) for display
    const netPlotArea = netAreaBase;

    // MHADA Required Check
    // Excel Formula: IF(H17>=4000,IF(M14="Y","NO","YES"),"NO")
    // H17 = Net Area Of Plot (which is netAreaBase in our code)
    // M14 = Redevelopment ("Is this 30 Year Old Redevelopment Building ?")
    // So: If NetArea >= 4000 AND Redevelopment is NO -> YES
    let mhadaRequired = "No";
    let openSpaceRequiredText = "No";
    // Check redevelopment status
    const isRedevelopment = inputs.redevelopment === 'Yes' || (typeof inputs.redevelopment === 'string' && inputs.redevelopment.startsWith('Yes'));

    if (netAreaBase >= 4000) {
      openSpaceRequiredText = "Yes";
      if (!isRedevelopment) {
        mhadaRequired = "Yes";
      }
    }

    // Exclude Redevelopment FSI from total permissible FSI
    // const totalFSI = baseFSI + paidFSI + tdrFSI + redevelopmentFSI + additionalFSI;

    // For Metro zones, use 75%/25% split for paid/TDR to avoid rounding issues
    let actualPaidFSI = paidFSI;
    let actualTdrFSI = tdrFSI;
    if (inputs.metroZone === 'Yes') {
      const additionalTotal = paidFSI + tdrFSI;
      // Use exact 75%/25% split as requested
      actualPaidFSI = additionalTotal * 0.75;
      actualTdrFSI = additionalTotal * 0.25;
    }

    const totalFSI = baseFSI + actualPaidFSI + actualTdrFSI;
    // Buildable area equals sum of computed areas (base + additions)

    // Metro-specific additional FSI split
    let metroExtra = null;
    if (inputs.metroZone === 'Yes') {
      const additionalTotal = paidFSI + tdrFSI;
      metroExtra = {
        additionalFSI: additionalTotal,
        premiumPercent: 0.75,
        tdrPercent: 0.25,
        premiumFSI: additionalTotal * 0.75,
        tdrFSI: additionalTotal * 0.25
      };
    }

    // Ancillary FSI Logic
    // Industrial/Commercial => 80% of Total Permissible FSI, Others => 60%
    // Excel Formula: IF(OR(M4="Industrial",M4="Commercial"),80%,60%)
    let ancillaryPercent = 0.60;
    if (inputs.buildingType === 'Industrial' || inputs.buildingType === 'Commercial') {
      ancillaryPercent = 0.80;
    }
    const ancillaryFSI = totalFSI * ancillaryPercent;

    // Total FSI Including Ancillary
    const totalFSIIncludingAncillary = totalFSI + ancillaryFSI;


    // Spreadsheet-style breakdown like the full calculator
    let breakdownPercents = {
      basePercent: calcConfig?.constants?.basePercent ?? 0.75,
      premiumPercent: calcConfig?.constants?.premiumPercent ?? 0.75,
      tdrPercent: calcConfig?.constants?.tdrPercent ?? 0.50,
      redevelopmentPercent: calcConfig?.constants?.redevelopmentPercent ?? 0.30,
      incentivePercent: calcConfig?.constants?.incentivePercent ?? 0.00
    };

    // Adjust percentages based on Full FSI Conditions
    const isSanctioned = !!inputs.sanctionedLayout;
    const isGunthewari = !!inputs.gunthewari;
    const isOpenSpaceLeft = !!inputs.openSpaceLeft;
    const isChallan = !!inputs.willingToPayChallan;

    // If Gunthewari is selected → set Basic and Premium to 100%
    if (isGunthewari) {
      breakdownPercents.basePercent = 1.0;
      breakdownPercents.premiumPercent = 1.0;
    }

    // If Sanctioned, Open Space Left, or Willing To Pay Challan → set Basic, Premium, TDR to 100%
    if (isSanctioned || isOpenSpaceLeft || isChallan) {
      breakdownPercents.basePercent = 1.0;
      breakdownPercents.premiumPercent = 1.0;
      breakdownPercents.tdrPercent = 1.0;
    }
    const baseArea = netAreaBase * breakdownPercents.basePercent * baseFSI;
    // Caution: Premium, TDR, Redevelopment, Incentive are applied on "Net Area Additions"
    // In our code netAreaAdditions was set to plotArea.
    // Let's verify if they should be partially deducted? 
    // Excel usually uses 'Net Area Of Plot' (Step 7) for Basic FSI.
    // For Additions (Premium, TDR), Excel references 'Item 2' (Area Of Plot) or similar?
    // Looking at Excel Formula for Premium FSI (Row 20): H20 = E20 * G20 * C20
    // C20 refers to C18?? cell-trace says C20 refers H7? H7 is 'Net Area Of Plot'? 
    // Wait, H7 in Step 7 is Net Area Of Plot.
    // Let's assume netAreaAdditions should be the same as netAreaBase for typical calculations 
    // unless the Excel says otherwise.
    // In existing code: const netAreaAdditions = plotArea;
    // Checking Excel output for Premium FSI formula: Formula=E20*G20*C20
    // C20 value is 331 (Same as Plot Area if no deductions?). 
    // Actually in Excel Step 7 'Net Area Of Plot' C523 is 331.
    // Let's trust existing logic for netAreaAdditions being full plot area OR netAreaBase?
    // Exisintg code used `plotArea`.
    // However, usually Premium is on the Net Plot Area (after road widening).
    // Let's keep `netAreaAdditions = plotArea` as per existing code to minimize disruption unless clearly wrong.

    const premiumArea = netAreaAdditions * breakdownPercents.premiumPercent * actualPaidFSI;
    const tdrArea = netAreaAdditions * breakdownPercents.tdrPercent * actualTdrFSI;
    const redevelopmentArea = netAreaAdditions * breakdownPercents.redevelopmentPercent * redevelopmentFSI;
    const incentiveArea = netAreaAdditions * breakdownPercents.incentivePercent * (additionalFSI || 0);
    // Exclude Redevelopment area from totals, but keep it visible in the breakdown
    const additionsTotalArea = premiumArea + tdrArea;
    const spreadsheetTotalArea = baseArea + additionsTotalArea;

    setCalculationResult({
      netPlotArea: plotArea,
      netPlotAreaSqft: sqmToSqft(plotArea),
      grossArea: grossArea,
      grossAreaSqft: sqmToSqft(grossArea),
      netPlotAreaAfterDeductions: netPlotArea,
      baseFSI,
      paidFSI: actualPaidFSI, // Use actual calculated values (75/25 split for metro)
      tdrFSI: actualTdrFSI, // Use actual calculated values (75/25 split for metro)
      redevelopmentFSI,
      additionalFSI,
      permissibleFSI: totalFSI,
      buildableArea: spreadsheetTotalArea,
      buildableAreaSqft: sqmToSqft(spreadsheetTotalArea),
      openSpaceRequired,
      openSpaceRequiredSqft: sqmToSqft(openSpaceRequired),
      amenitySpaceRequired,
      amenitySpaceRequiredSqft: sqmToSqft(amenitySpaceRequired),
      mhadaRequired, /* New Field */
      openSpaceRequiredText, /* New Field */
      ancillaryFSI,
      totalFSIIncludingAncillary,
      ancillaryPercent, // Add this to state to access in UI
      breakdown: {
        percents: breakdownPercents,
        netAreaBase: netAreaBase,
        netAreaAdditions: netAreaAdditions,
        baseArea,
        premiumArea,
        tdrArea,
        redevelopmentArea,
        incentiveArea,
        additionsTotalArea,
        spreadsheetTotalArea
      },
      metroExtra
    });

    setShowResults(true);
  };

  const handleWhatsAppShare = () => {
    if (!calculationResult) return;

    const b = calculationResult.breakdown.percents;
    const a = calculationResult.breakdown;

    const messageLines = [
      "Hello, I would like to know more about this FSI Calculation Result in detail.",
      "",
      "*🏢 FSI Calculation Results Summary*",
      "---------------------------------------",
      "",
      "*📊 Area Details*",
      `• Plot Area: *${calculationResult.netPlotArea.toFixed(2)} sqm* (${sqmToSqft(calculationResult.netPlotArea).toFixed(2)} sqft)`,
      `• Net Plot Area (after deductions): *${calculationResult.netPlotAreaAfterDeductions.toFixed(2)} sqm* (${sqmToSqft(calculationResult.netPlotAreaAfterDeductions).toFixed(2)} sqft)`,
      "",
      "*🏛️ Project Requirements & Deductions*",
      `• Amenity Space Required (5%): *${calculationResult.amenitySpaceRequired > 0 ? calculationResult.amenitySpaceRequired.toFixed(2) + ' sqm' : '0.00 sqm'}*`,
      `• MHADA Required: *${calculationResult.mhadaRequired}*`,
      `• Open Space Required (10%): *${calculationResult.openSpaceRequiredText}*`,
      "",
      "*📈 FSI Breakdown*",
      `• Basic FSI (${(b.basePercent * 100).toFixed(0)}%): *${a.baseArea.toFixed(2)} sqm* (Factor: ${calculationResult.baseFSI.toFixed(3)})`,
      `• Premium FSI (${(b.premiumPercent * 100).toFixed(0)}%): *${a.premiumArea.toFixed(2)} sqm* (Factor: ${calculationResult.paidFSI.toFixed(3)})`,
      `• TDR Area (${(b.tdrPercent * 100).toFixed(0)}%): *${a.tdrArea.toFixed(2)} sqm* (Factor: ${calculationResult.tdrFSI.toFixed(3)})`,
      `• Redevelopment FSI (${(b.redevelopmentPercent * 100).toFixed(0)}%): *${a.redevelopmentArea.toFixed(2)} sqm* (Factor: ${calculationResult.redevelopmentFSI.toFixed(3)})`,
      "",
      "*💎 Final Totals*",
      `• Total Permissible FSI (Factor): *${calculationResult.permissibleFSI.toFixed(2)}*`,
      `• Total Max Permissible FSI (Area): *${a.spreadsheetTotalArea.toFixed(2)} sqm*`,
      `• Ancillary FSI (${(calculationResult.ancillaryPercent * 100).toFixed(0)}%): *${(a.spreadsheetTotalArea * calculationResult.ancillaryPercent).toFixed(2)} sqm*`,
      "---------------------------------------",
      `*🎯 Total FSI Including Ancillary: ${(a.spreadsheetTotalArea * (1 + calculationResult.ancillaryPercent)).toFixed(2)} sqm* (${(a.spreadsheetTotalArea * (1 + calculationResult.ancillaryPercent) * 10.764).toFixed(2)} sqft)`,
      "",
      "Please let me know how we can proceed."
    ];

    const message = messageLines.join('\n');
    const whatsappUrl = `https://wa.me/918003989393?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const resetCalculator = () => {
    setInputs({
      plotArea: '',
      areaType: 'Non-Congested',
      metroZone: 'No',
      roadWidth: '',
      buildingType: 'Residential',
      sanctionedLayout: false,
      gunthewari: false,
      openSpaceLeft: false,
      willingToPayChallan: false,
      redevelopment: 'No',
      previouslyConsumedBuiltUpArea: '',
      numberOfTenements: '',
      deductionRoadWidening: '',
      deductionDPRoad: '',
      deductionReservation: ''
    });
    setShowResults(false);
    setCalculationResult(null);
    setErrors({});
  };

  return (
    <section id="calculator" className="py-16 bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="max-w mx-auto px-4">
        <div className="text-center mb-12 animate-fade-in-up">
          <h2 className="font-headline text-3xl font-bold text-card-foreground mb-4">
            Smart Construction Tools
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Professional-grade calculators and tools designed specifically for Pune's construction industry.
            Get accurate FSI calculations, cost estimates, and regulatory compliance insights.
          </p>
        </div>

        <div className="max-w-7xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {/* Calculator Header */}
          <div className="flex justify-center mb-8">
            <div className="flex items-center space-x-3 px-6 py-4 rounded-xl bg-primary text-white construction-shadow hover:scale-105 transition-transform duration-300">
              <Icon name="Building" size={20} />
              <div className="text-left">
                <div className="font-semibold">FSI Calculator</div>
                <div className="text-xs text-white/80">2,500+ calculations this month</div>
              </div>
            </div>
          </div>

          {/* Calculator Interface */}
          <div className="bg-card rounded-2xl construction-shadow p-8 hover:shadow-xl transition-shadow duration-300">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Left: Input Section */}
              <div className="lg:col-span-1">
                <h3 className="font-headline text-xl font-bold text-foreground mb-6">
                  Floor Space Index Calculator
                </h3>
                <p className="text-muted-foreground mb-6">
                  Calculate Floor Space Index with real Pune zoning data and get detailed construction insights
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  {/* 1. Plot Area */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Plot Area (sq.m)</label>
                    <input
                      type="number"
                      placeholder="Enter plot area"
                      name="plotArea"
                      value={inputs.plotArea}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    {errors.plotArea && <p className="text-xs text-red-500 mt-1">{errors.plotArea}</p>}
                  </div>

                  {/* 2. Area Type */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Area Type</label>
                    <select
                      name="areaType"
                      value={inputs.areaType}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-border rounded-lg"
                    >
                      <option>Non-Congested</option>
                      <option>Congested</option>
                    </select>
                  </div>

                  {/* 3. Metro/TOD */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Metro / TOD Zone</label>
                    <select
                      name="metroZone"
                      value={inputs.metroZone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-border rounded-lg"
                    >
                      <option>No</option>
                      <option>Yes</option>
                    </select>
                  </div>

                  {/* 4. Road Width */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Access Road Width (m)</label>
                    <input
                      type="number"
                      name="roadWidth"
                      placeholder="e.g. 9"
                      value={inputs.roadWidth}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-border rounded-lg"
                    />
                    {errors.roadWidth && <p className="text-xs text-red-500 mt-1">{errors.roadWidth}</p>}
                  </div>

                  {/* 5. Building Type and Redevelopment */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Building Type</label>
                    <select
                      name="buildingType"
                      value={inputs.buildingType}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-border rounded-lg"
                    >
                      <option>Residential</option>
                      <option>Commercial</option>
                      <option>Mixed</option>
                      <option>Industrial</option>
                    </select>
                  </div>

                  {/* 6. Redevelopment */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Redevelopment</label>
                    <select
                      name="redevelopment"
                      value={inputs.redevelopment}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-border rounded-lg"
                    >
                      <option>No</option>
                      <option>Yes (30+ years old)</option>
                    </select>
                  </div>

                  {/* Redevelopment Note - inputs removed per requirement */}
                  {inputs.redevelopment === 'Yes' || (typeof inputs.redevelopment === 'string' && inputs.redevelopment.startsWith('Yes')) ? (
                    <div className="md:col-span-2 bg-muted/20 rounded-lg p-3">
                      <h4 className="font-medium mb-2 text-sm">Redevelopment Details</h4>
                      <p className="text-xs text-muted-foreground">
                        Note: Whichever is more—(a) Previously consumed built-up area × 30% or (b) No. of tenements × 15—will be rewarded in the form of TDR.
                      </p>
                    </div>
                  ) : null}

                  {/* Full FSI Conditions */}
                  <div className="md:col-span-2 bg-muted/20 rounded-lg p-3">
                    <h4 className="font-medium mb-2 text-sm">Full FSI Conditions</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="sanctionedLayout"
                          checked={inputs.sanctionedLayout}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary"
                        />
                        <span>Sanctioned Layout</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="gunthewari"
                          checked={inputs.gunthewari}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary"
                        />
                        <span>Gunthewari</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="openSpaceLeft"
                          checked={inputs.openSpaceLeft}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary"
                        />
                        <span>Open Space Left</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          name="willingToPayChallan"
                          checked={inputs.willingToPayChallan}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary"
                        />
                        <span>Willing To Pay Challan</span>
                      </label>
                    </div>
                  </div>

                  {/* Deductions */}
                  <div className="md:col-span-2 bg-muted/20 rounded-lg p-3">
                    <h4 className="font-medium mb-2 text-sm">Deductions (sq.m)</h4>
                    <div className="flex flex-nowrap gap-3 text-sm items-end">
                      <div className="flex-1 min-w-0 flex flex-col">
                        <label className="text-xs text-muted-foreground mb-1 whitespace-normal break-words min-h-[2.5rem]">
                          Road widening (non-DP road)
                        </label>
                        <input
                          type="number"
                          placeholder="0"
                          name="deductionRoadWidening"
                          value={inputs.deductionRoadWidening}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-border rounded"
                        />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <label className="text-xs text-muted-foreground mb-1 whitespace-normal break-words min-h-[2.5rem]">
                          DP Road Area
                        </label>
                        <input
                          type="number"
                          placeholder="0"
                          name="deductionDPRoad"
                          value={inputs.deductionDPRoad}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-border rounded"
                        />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <label className="text-xs text-muted-foreground mb-1 whitespace-normal break-words min-h-[2.5rem]">
                          Reservation
                        </label>
                        <input
                          type="number"
                          placeholder="0"
                          name="deductionReservation"
                          value={inputs.deductionReservation}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-border rounded"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Note: If reservation of Green Belt or HEMRL Zone is there, it will not get
                      deducted nor affect FSI.
                    </p>
                  </div>

                  <div className="md:col-span-2 flex space-x-3 pt-2">
                    <Button
                      variant="default"
                      onClick={calculateFSI}
                      disabled={!inputs.plotArea || !inputs.roadWidth}
                      iconName="Calculator"
                      iconPosition="left"
                      className="flex-1"
                    >
                      Calculate FSI
                    </Button>
                    <Button
                      variant="outline"
                      onClick={resetCalculator}
                      iconName="RotateCcw"
                      iconPosition="left"
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>

              {/* Right: Results Panel */}
              <div className="lg:col-span-2">
                <div className="bg-muted/30 rounded-2xl p-4 lg:p-6 h-full border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-headline font-semibold text-lg">Results</h4>
                    <Icon name="BarChart2" size={18} className="text-primary" />
                  </div>

                  {!showResults || !calculationResult ? (
                    <div className="h-full min-h-[260px] flex items-center justify-center text-center text-muted-foreground">
                      <div>
                        <Icon name="Calculator" size={28} className="mx-auto mb-2 opacity-70" />
                        <p>Enter inputs on the left and click Calculate to see results.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-primary/5 rounded-lg p-5 border border-primary/20 text-center">
                        <div className="text-4xl font-bold text-primary mb-1">
                          {calculationResult.permissibleFSI.toFixed(2)}
                        </div>
                        <div className="text-sm text-muted-foreground">Total Permissible FSI</div>
                      </div>

                      <div className="grid grid-cols-1 gap-4">

                        <div className="bg-muted/50 rounded-lg p-0 border border-border overflow-hidden">
                          <h5 className="p-3 font-semibold bg-primary/10 border-b border-primary/10">Project Requirements & Deductions</h5>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-muted/50 text-muted-foreground font-medium">
                                <tr>
                                  <th className="p-2 md:p-3 text-left">Component</th>
                                  <th className="p-2 md:p-3 text-right">Area (SQM)</th>
                                  <th className="p-2 md:p-3 text-right">Area (SQFT)</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border/50">
                                {/* <tr>
                                  <td className="p-2 md:p-3">Buildable Area</td>
                                  <td className="p-2 md:p-3 text-right font-medium">{calculationResult.buildableArea.toFixed(2)}</td>
                                  <td className="p-2 md:p-3 text-right text-muted-foreground">{calculationResult.buildableAreaSqft.toFixed(2)}</td>
                                </tr> */}
                                <tr>
                                  <td className="p-2 md:p-3">Net Plot Area <span className="text-xs text-muted-foreground block">(after deductions)</span></td>
                                  <td className="p-2 md:p-3 text-right font-medium">{calculationResult.netPlotAreaAfterDeductions.toFixed(2)}</td>
                                  <td className="p-2 md:p-3 text-right text-muted-foreground">{sqmToSqft(calculationResult.netPlotAreaAfterDeductions).toFixed(2)}</td>
                                </tr>

                                {calculationResult.amenitySpaceRequired > 0 && (
                                  <tr className="bg-green-50/50">
                                    <td className="p-2 md:p-3">
                                      Amenity Space Required (5%)
                                      <span className="text-[10px] md:text-xs text-muted-foreground block">Required because gross area &gt;= 20,000 sq.m</span>
                                    </td>
                                    <td className="p-2 md:p-3 text-right font-medium text-green-700">{calculationResult.amenitySpaceRequired.toFixed(2)}</td>
                                    <td className="p-2 md:p-3 text-right text-muted-foreground">{calculationResult.amenitySpaceRequiredSqft.toFixed(2)}</td>
                                  </tr>
                                )}
                                <tr>
                                  <td className="p-2 md:p-3">MHADA Required</td>
                                  <td colSpan="2" className="p-2 md:p-3 text-center font-medium">
                                    {calculationResult.mhadaRequired}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="p-2 md:p-3">Open Space Required (10%)
                                    <span className="text-[10px] md:text-xs text-muted-foreground block">Required because gross area &gt;= 4,000 sq.m</span>
                                  </td>
                                  <td colSpan="2" className="p-2 md:p-3 text-center font-medium">
                                    {calculationResult.openSpaceRequiredText}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        {/* {calculationResult.metroExtra && (
                          <div className="bg-muted/50 rounded-lg p-4 border border-border">
                            <div className="font-medium mb-2">In case of Metro</div>
                            <div className="grid grid-cols-2 text-sm">
                              <div className="text-muted-foreground">Additional FSI</div>
                              <div className="text-right">{calculationResult.metroExtra.additionalFSI.toFixed(2)}</div>
                              <div className="text-muted-foreground">Premium FSI <span className="ml-1 text-xs">75%</span></div>
                              <div className="text-right">{calculationResult.metroExtra.premiumFSI.toFixed(3)}</div>
                              <div className="text-muted-foreground">TDR <span className="ml-1 text-xs">25%</span></div>
                              <div className="text-right">{calculationResult.metroExtra.tdrFSI.toFixed(3)}</div>
                            </div>
                          </div>
                        )} */}
                        {/* Spreadsheet-style breakdown */}
                        <div className="bg-muted/50 rounded-lg p-0 border border-border overflow-hidden">
                          <div className="overflow-x-auto lg:overflow-visible">
                            <div className="min-w-[800px] lg:min-w-0">
                              <div className="grid grid-cols-7 text-[10px] lg:text-xs font-medium bg-muted/70 border-b border-border">
                                <div className="p-2">Item</div>
                                <div className="p-2 text-right">Net Area (SQM)</div>
                                <div className="p-2 text-right">Net Area (SQFT)</div>
                                <div className="p-2 text-right">Percent</div>
                                <div className="p-2 text-right">Factor</div>
                                <div className="p-2 text-right">Result (SQM)</div>
                                <div className="p-2 text-right">Result (SQFT)</div>
                              </div>
                              <div className="grid grid-cols-7 text-[10px] lg:text-xs border-b border-border">
                                <div className="p-2">Basic FSI</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.netAreaBase.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.netAreaBase * 10.764).toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.percents.basePercent * 100).toFixed(0)}%</div>
                                <div className="p-2 text-right">{calculationResult.baseFSI.toFixed(3)}</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.baseArea.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.baseArea * 10.764).toFixed(2)}</div>
                              </div>
                              <div className="grid grid-cols-7 text-[10px] lg:text-xs border-b border-border">
                                <div className="p-2">a. Premium FSI</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.netAreaAdditions.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.netAreaAdditions * 10.764).toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.percents.premiumPercent * 100).toFixed(0)}%</div>
                                <div className="p-2 text-right">{calculationResult.paidFSI.toFixed(3)}</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.premiumArea.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.premiumArea * 10.764).toFixed(2)}</div>
                              </div>
                              <div className="grid grid-cols-7 text-[10px] lg:text-xs border-b border-border">
                                <div className="p-2">b. TDR area</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.netAreaAdditions.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.netAreaAdditions * 10.764).toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.percents.tdrPercent * 100).toFixed(0)}%</div>
                                <div className="p-2 text-right">{calculationResult.tdrFSI.toFixed(3)}</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.tdrArea.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.tdrArea * 10.764).toFixed(2)}</div>
                              </div>
                              <div className="grid grid-cols-7 text-[10px] lg:text-xs border-b border-border">
                                <div className="p-2">c. Redevelopment FSI</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.netAreaAdditions.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.netAreaAdditions * 10.764).toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.percents.redevelopmentPercent * 100).toFixed(0)}%</div>
                                <div className="p-2 text-right">{calculationResult.redevelopmentFSI.toFixed(3)}</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.redevelopmentArea.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.redevelopmentArea * 10.764).toFixed(2)}</div>
                              </div>
                              <div className="grid grid-cols-7 text-[10px] lg:text-xs font-semibold">
                                <div className="p-2">Total Maximum Permissible FSI (area)</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">{calculationResult.breakdown.spreadsheetTotalArea.toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.spreadsheetTotalArea * 10.764).toFixed(2)}</div>
                              </div>
                              <div className="grid grid-cols-7 text-[10px] lg:text-xs border-b border-border">
                                <div className="p-2">Ancillary FSI</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">{(calculationResult.ancillaryPercent * 100).toFixed(0)}%</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.spreadsheetTotalArea * calculationResult.ancillaryPercent).toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.spreadsheetTotalArea * calculationResult.ancillaryPercent * 10.764).toFixed(2)}</div>
                              </div>
                              <div className="grid grid-cols-7 text-[10px] lg:text-xs font-bold bg-primary/5">
                                <div className="p-2">Total FSI Including Ancillary FSI</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">&nbsp;</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.spreadsheetTotalArea * (1 + calculationResult.ancillaryPercent)).toFixed(2)}</div>
                                <div className="p-2 text-right">{(calculationResult.breakdown.spreadsheetTotalArea * (1 + calculationResult.ancillaryPercent) * 10.764).toFixed(2)}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 flex justify-center">
                        <Button
                          type="button"
                          variant="default"
                          onClick={handleWhatsAppShare}
                          className="bg-[#25D366] hover:bg-[#128C7E] text-white border-0 w-full sm:w-auto text-sm md:text-base font-medium shadow-md transition-all duration-300"
                          iconName="MessageCircle"
                          iconPosition="left"
                        >
                          Know more about this result in detail
                        </Button>
                      </div>

                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-12">
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8">
            <h3 className="font-headline text-2xl font-bold text-card-foreground mb-4">
              Need Professional Help?
            </h3>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
              Get expert consultation for your construction project with our professional services.
              Our team of experienced architects and engineers can help you with FSI calculations,
              project planning, and regulatory compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services-hub-professional-network-directory">
                <Button variant="default" size="lg" iconName="Users" iconPosition="left">
                  Book Consultation
                </Button>
              </Link>
              <Link to="/projects">
                <Button variant="outline" size="lg" iconName="Building" iconPosition="left">
                  View Our Projects
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SmartToolsPreview;
